import React, { useEffect, useState, useCallback } from 'react';
import vercelLogger from '../utils/vercelRuntimeLogger';

interface ProductionDebugPanelProps {
  enabled?: boolean;
}

interface LogEntry {
  timestamp: string;
  level: string;
  component: string;
  action: string;
  data?: any;
  error?: any;
}

const ProductionDebugPanel: React.FC<ProductionDebugPanelProps> = ({ 
  enabled = false 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<'all' | 'error' | 'warn' | 'info'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRecording, setIsRecording] = useState(true);
  const [networkRequests, setNetworkRequests] = useState<any[]>([]);

  useEffect(() => {
    // Only enable in production with special flag or query param
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') === 'true' || 
                     localStorage.getItem('debugMode') === 'true';
    
    if (!enabled && !debugMode) return;

    // Intercept console methods to capture logs
    const originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
    };

    const captureLog = (level: string) => (...args: any[]) => {
      if (isRecording) {
        const logEntry: LogEntry = {
          timestamp: new Date().toISOString(),
          level,
          component: 'Console',
          action: 'LOG',
          data: args.length === 1 ? args[0] : args,
        };
        
        setLogs(prev => [...prev.slice(-99), logEntry]);
      }
      
      // Call original console method
      (originalConsole as any)[level](...args);
    };

    console.log = captureLog('info');
    console.info = captureLog('info');
    console.warn = captureLog('warn');
    console.error = captureLog('error');

    // Intercept fetch to track network requests
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = Date.now();
      const [url, options] = args;
      
      const request = {
        id: `req_${Date.now()}`,
        url: typeof url === 'string' ? url : url.toString(),
        method: options?.method || 'GET',
        startTime: new Date().toISOString(),
        status: 'pending',
      };
      
      setNetworkRequests(prev => [...prev.slice(-49), request]);
      
      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;
        
        setNetworkRequests(prev => 
          prev.map(req => 
            req.id === request.id 
              ? { ...req, status: response.status, duration, ok: response.ok }
              : req
          )
        );
        
        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        setNetworkRequests(prev => 
          prev.map(req => 
            req.id === request.id 
              ? { ...req, status: 'error', duration, error: errorMessage }
              : req
          )
        );
        
        throw error;
      }
    };

    // Keyboard shortcuts
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setIsVisible(prev => !prev);
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        setLogs([]);
        setNetworkRequests([]);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      // Restore original methods
      console.log = originalConsole.log;
      console.info = originalConsole.info;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      window.fetch = originalFetch;
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [enabled, isRecording]);

  const filteredLogs = logs.filter(log => {
    if (filter !== 'all' && log.level !== filter) return false;
    if (searchTerm && !JSON.stringify(log).toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const exportDebugInfo = useCallback(() => {
    const debugInfo = {
      sessionId: vercelLogger.getSessionInfo().sessionId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      logs: filteredLogs,
      networkRequests,
      localStorage: { ...localStorage },
      sessionStorage: { ...sessionStorage },
    };

    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-info-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredLogs, networkRequests]);

  const sendToSupport = useCallback(async () => {
    try {
      // Export logs for support
      const exportData = vercelLogger.exportLogs();
      console.log('Debug information exported:', exportData);
      alert('Debug information sent to support team. Session ID: ' + vercelLogger.getSessionInfo().sessionId);
    } catch (error) {
      console.error('Failed to send debug info:', error);
    }
  }, []);

  if (!isVisible) {
    // Floating debug button
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 w-12 h-12 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-800 transition-colors z-50 flex items-center justify-center"
        title="Open Debug Panel (Ctrl+Shift+D)"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-0 right-0 z-50 ${isMinimized ? 'w-64' : 'w-full md:w-2/3 lg:w-1/2'} h-96 bg-gray-900 text-white shadow-2xl transition-all`}>
      {/* Header */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`} />
          <span className="font-mono text-sm">Production Debug Console</span>
          <span className="text-xs text-gray-400">Session: {vercelLogger.getSessionInfo().sessionId.substring(0, 8)}...</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsRecording(!isRecording)}
            className="text-xs px-2 py-1 bg-gray-700 rounded hover:bg-gray-600"
          >
            {isRecording ? 'Pause' : 'Record'}
          </button>
          <button
            onClick={exportDebugInfo}
            className="text-xs px-2 py-1 bg-gray-700 rounded hover:bg-gray-600"
            title="Export Debug Info"
          >
            Export
          </button>
          <button
            onClick={sendToSupport}
            className="text-xs px-2 py-1 bg-blue-600 rounded hover:bg-blue-500"
            title="Send to Support"
          >
            Send to Support
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-gray-400 hover:text-white"
          >
            {isMinimized ? '□' : '—'}
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Toolbar */}
          <div className="bg-gray-850 px-4 py-2 flex items-center gap-4 border-b border-gray-700">
            <div className="flex gap-2">
              {(['all', 'error', 'warn', 'info'] as const).map(level => (
                <button
                  key={level}
                  onClick={() => setFilter(level)}
                  className={`text-xs px-2 py-1 rounded ${
                    filter === level ? 'bg-gray-700' : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                  {level !== 'all' && (
                    <span className="ml-1 text-gray-500">
                      ({logs.filter(l => l.level === level).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-gray-800 text-white text-xs px-2 py-1 rounded border border-gray-700 focus:border-gray-500 outline-none"
            />
            <button
              onClick={() => {
                setLogs([]);
                setNetworkRequests([]);
              }}
              className="text-xs px-2 py-1 bg-red-600 rounded hover:bg-red-500"
            >
              Clear All
            </button>
          </div>

          {/* Content */}
          <div className="flex h-full">
            {/* Logs */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredLogs.length === 0 ? (
                <div className="text-gray-500 text-xs text-center py-4">
                  No logs to display. Press Ctrl+Shift+C to clear.
                </div>
              ) : (
                filteredLogs.map((log, index) => (
                  <div
                    key={`${log.timestamp}-${index}`}
                    className={`text-xs font-mono p-2 rounded ${
                      log.level === 'error' ? 'bg-red-900/30 border-l-2 border-red-500' :
                      log.level === 'warn' ? 'bg-yellow-900/30 border-l-2 border-yellow-500' :
                      'bg-gray-800/50 border-l-2 border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 flex-shrink-0">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`font-bold ${
                        log.level === 'error' ? 'text-red-400' :
                        log.level === 'warn' ? 'text-yellow-400' :
                        'text-blue-400'
                      }`}>
                        [{log.level.toUpperCase()}]
                      </span>
                      <span className="text-cyan-400">{log.component}</span>
                      <span className="text-white flex-1">{log.action}</span>
                    </div>
                    {log.data && (
                      <details className="mt-1 ml-16">
                        <summary className="text-gray-400 cursor-pointer hover:text-white">
                          Data
                        </summary>
                        <pre className="text-gray-300 text-xs mt-1 overflow-x-auto">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      </details>
                    )}
                    {log.error && (
                      <div className="text-red-400 ml-16 mt-1">
                        {log.error.message || log.error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Network Requests */}
            <div className="w-1/3 border-l border-gray-700 overflow-y-auto p-2">
              <h3 className="text-xs font-bold mb-2 text-gray-400">Network</h3>
              {networkRequests.map((req, index) => (
                <div
                  key={req.id}
                  className={`text-xs font-mono p-1 mb-1 rounded ${
                    req.status === 'error' ? 'bg-red-900/30' :
                    req.status === 'pending' ? 'bg-yellow-900/30' :
                    req.ok === false ? 'bg-orange-900/30' :
                    'bg-green-900/30'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span className={`${
                      req.status === 'error' ? 'text-red-400' :
                      req.status === 'pending' ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {req.status === 'pending' ? '⏳' : req.status === 'error' ? '❌' : '✓'}
                    </span>
                    <span className="text-gray-400">{req.method}</span>
                    <span className="text-white truncate flex-1" title={req.url}>
                      {req.url.replace(window.location.origin, '')}
                    </span>
                    {req.duration && (
                      <span className="text-gray-500">{req.duration}ms</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductionDebugPanel;